import { Routes, Route, Navigate } from 'react-router-dom';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from './store/auth';
import { LoginPage } from './pages/Login';
import { ImpressumPage } from './pages/Impressum';
import { DatenschutzPage } from './pages/Datenschutz';
import { DashboardPage } from './pages/Dashboard';
import { ProjectsPage } from './pages/Projects';
import { ProjectDetailPage } from './pages/ProjectDetail';
import { PostsPage } from './pages/Posts';
import { AdminPage } from './pages/Admin';
import { AdminTenantsPage } from './pages/AdminTenants';
import { TenantsPage } from './pages/Tenants';
import { ProfilePage } from './pages/Profile';
import { MediaPage } from './pages/Media';
import CreatorStudio from './pages/CreatorStudio';
import { AIReview } from './pages/AIReview';
import { FlightsPage } from './pages/Flights';
import { FlightDetailPage } from './pages/FlightDetail';
import { Trash } from './pages/Trash';
import { MarketingPage } from './pages/Marketing';
import { MarketingPillarsPage } from './pages/MarketingPillars';
import { MarketingCalendarPage } from './pages/MarketingCalendar';
import { MarketingTemplatesPage } from './pages/MarketingTemplates';
import { MetaOverview } from './pages/MetaOverview';
import Analytics from './pages/Analytics';
import AnalyticsTrends from './pages/AnalyticsTrends';
import AnalyticsCompare from './pages/AnalyticsCompare';
import { Layout } from './components/Layout';

// Protected Route wrapper
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation('common');
  const { isAuthenticated, isLoading } = useAuthStore();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-gray-500">{t('loading')}</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

// Page wrappers with Layout
function DashboardLayout() {
  return <Layout><DashboardContent /></Layout>;
}

function ProjectsLayout() {
  return <Layout><ProjectsContent /></Layout>;
}

function ProjectDetailLayout() {
  return <Layout><ProjectDetailContent /></Layout>;
}

function AdminLayout() {
  return <Layout><AdminContent /></Layout>;
}

function MediaLayout() {
  return <Layout><MediaContent /></Layout>;
}

// Content components (pages without their own headers)
function DashboardContent() {
  // We'll need to modify the pages to export content-only versions
  // For now, we'll keep them as-is but refactor later
  return <DashboardPage />;
}

function ProjectsContent() {
  return <ProjectsPage />;
}

function ProjectDetailContent() {
  return <ProjectDetailPage />;
}

function AdminContent() {
  return <AdminPage />;
}

function AdminTenantsLayout() {
  return <Layout><AdminTenantsPage /></Layout>;
}

function MediaContent() {
  return <MediaPage />;
}

function CreatorLayout() {
  return <Layout><CreatorStudio /></Layout>;
}

function AIReviewLayout() {
  return <Layout><AIReview /></Layout>;
}

function FlightsLayout() {
  return <Layout><FlightsPage /></Layout>;
}

function FlightDetailLayout() {
  return <Layout><FlightDetailPage /></Layout>;
}

function PostsLayout() {
  return <Layout><PostsPage /></Layout>;
}

function TrashLayout() {
  return <Layout><Trash /></Layout>;
}

function MarketingLayout() {
  return <Layout><MarketingPage /></Layout>;
}

function MarketingPillarsLayout() {
  return <Layout><MarketingPillarsPage /></Layout>;
}

function MarketingCalendarLayout() {
  return <Layout><MarketingCalendarPage /></Layout>;
}

function MarketingTemplatesLayout() {
  return <Layout><MarketingTemplatesPage /></Layout>;
}

function MetaOverviewLayout() {
  return <Layout><MetaOverview /></Layout>;
}

function AnalyticsLayout() {
  return <Layout><Analytics /></Layout>;
}

function AnalyticsTrendsLayout() {
  return <Layout><AnalyticsTrends /></Layout>;
}

function AnalyticsCompareLayout() {
  return <Layout><AnalyticsCompare /></Layout>;
}

function App() {
  const { checkAuth } = useAuthStore();

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/impressum" element={<ImpressumPage />} />
      <Route path="/datenschutz" element={<DatenschutzPage />} />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <DashboardLayout />
          </ProtectedRoute>
        }
      />
      <Route
        path="/projects"
        element={
          <ProtectedRoute>
            <ProjectsLayout />
          </ProtectedRoute>
        }
      />
      <Route
        path="/projects/:id"
        element={
          <ProtectedRoute>
            <ProjectDetailLayout />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <AdminLayout />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/tenants"
        element={
          <ProtectedRoute>
            <AdminTenantsLayout />
          </ProtectedRoute>
        }
      />
      <Route
        path="/media"
        element={
          <ProtectedRoute>
            <MediaLayout />
          </ProtectedRoute>
        }
      />
      <Route
        path="/posts"
        element={
          <ProtectedRoute>
            <PostsLayout />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <ProfilePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/projects/:projectId/creator"
        element={
          <ProtectedRoute>
            <CreatorStudio />
          </ProtectedRoute>
        }
      />
      <Route
        path="/creator"
        element={
          <ProtectedRoute>
            <CreatorLayout />
          </ProtectedRoute>
        }
      />
      <Route
        path="/projects/:projectId/ai-review"
        element={
          <ProtectedRoute>
            <AIReviewLayout />
          </ProtectedRoute>
        }
      />
      <Route
        path="/flights"
        element={
          <ProtectedRoute>
            <FlightsLayout />
          </ProtectedRoute>
        }
      />
      <Route
        path="/flights/:flightId"
        element={
          <ProtectedRoute>
            <FlightDetailLayout />
          </ProtectedRoute>
        }
      />
      <Route
        path="/trash"
        element={
          <ProtectedRoute>
            <TrashLayout />
          </ProtectedRoute>
        }
      />
      <Route
        path="/marketing/pillars"
        element={
          <ProtectedRoute>
            <MarketingPillarsLayout />
          </ProtectedRoute>
        }
      />
      <Route
        path="/marketing/calendar"
        element={
          <ProtectedRoute>
            <MarketingCalendarLayout />
          </ProtectedRoute>
        }
      />
      <Route
        path="/marketing/templates"
        element={
          <ProtectedRoute>
            <MarketingTemplatesLayout />
          </ProtectedRoute>
        }
      />
      <Route
        path="/marketing"
        element={
          <ProtectedRoute>
            <MarketingLayout />
          </ProtectedRoute>
        }
      />
      <Route
        path="/meta"
        element={
          <ProtectedRoute>
            <MetaOverviewLayout />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tenants"
        element={
          <ProtectedRoute>
            <TenantsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/marketing/analytics"
        element={
          <ProtectedRoute>
            <AnalyticsLayout />
          </ProtectedRoute>
        }
      />
      <Route
        path="/marketing/analytics/trends"
        element={
          <ProtectedRoute>
            <AnalyticsTrendsLayout />
          </ProtectedRoute>
        }
      />
      <Route
        path="/marketing/analytics/compare"
        element={
          <ProtectedRoute>
            <AnalyticsCompareLayout />
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default App;
